import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { PublicPeopleTable } from '@/components/people/PublicPeopleTable';
import { OrgGraphTab } from '@/components/people/OrgGraphTab';
import { OrgTreeTab } from '@/components/people/OrgTreeTab';
import { peopleApi } from '@/services/people-api';
import { cn, getErrorMessage } from '@/lib/utils';
import type { IPublicPersonListItem } from '@skystone/shared';

type TabKey = 'people' | 'org-graph' | 'org-tree';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'people', label: 'People' },
  { key: 'org-graph', label: 'Org Graph' },
  { key: 'org-tree', label: 'Org Tree' },
];

// Load the full directory so the table can paginate client-side. Fetch in
// pages and accumulate until everything is loaded (PRODOPSAI-492) — a single
// fixed page would silently truncate a directory larger than the cap.
// SAFETY_CEILING bounds a pathological loop; the directory is ~170 today.
const FETCH_PAGE_SIZE = 200;
const SAFETY_CEILING = 5000;

export function PeopleDirectoryPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('people');
  const [items, setItems] = useState<IPublicPersonListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPeople = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const all: IPublicPersonListItem[] = [];
      let page = 1;
      // Fetch successive pages until we've loaded the full match set (or hit
      // the safety ceiling). For ~170 people this is a single request.
      for (;;) {
        const result = await peopleApi.list({ page, pageSize: FETCH_PAGE_SIZE });
        all.push(...result.items);
        const done =
          result.items.length === 0 ||
          all.length >= result.total ||
          all.length >= SAFETY_CEILING;
        if (done) break;
        page += 1;
      }
      setItems(all);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load people'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  return (
    <PageContainer variant="wide" className="space-y-6 p-6">
      <PageHeader title="People" subtitle="Directory of people from Microsoft Entra." />

      <div className="flex gap-1 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === tab.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'people' && (
        <Card>
          <CardContent className="pt-6">
            {isLoading && <PageLoading />}
            {error && !isLoading && <ErrorBanner message={error} />}
            {!isLoading && !error && <PublicPeopleTable items={items} />}
          </CardContent>
        </Card>
      )}

      {activeTab === 'org-graph' && <OrgGraphTab />}

      {activeTab === 'org-tree' && <OrgTreeTab />}
    </PageContainer>
  );
}
